/**
 * CrudManager — component กลางสำหรับทุกหน้า admin ที่เป็น "ทะเบียนข้อมูล" (CRUD)
 *
 * ทำไมต้องมี: หน้า Faq/Colors/Sizes/Triggers/Intents/Brands/Admins ฯลฯ ทำงานเหมือนกันหมด
 * (list + ฟอร์มเพิ่ม/แก้บนสุด + search + เลขลำดับ + แก้/ลบ/เปิด-ปิด) → เดิม copy โค้ดซ้ำทุกหน้า
 * รวมมาที่เดียว ขับด้วย schema (fields[]) → แต่ละหน้าเหลือแค่ประกาศ field + ตัวแสดงผลแถว
 *
 * มาตรฐาน UI (CLAUDE.md): ปุ่ม "เพิ่ม" + ฟอร์มบนสุด, เลขลำดับ, help text ทุก field,
 * ภาษาไทย/emoji, ปุ่มแก้/ลบ/เปิด-ปิด ชัดเจน, ค้นหาได้ถ้า > 20 แถว
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ImageAttach } from '@/components/ImageAttach';
import { useBrands, brandLabel, brandEmoji } from '@/lib/useBrands';
import type { Brand } from '@/lib/admin-api';

export type FieldType = 'text' | 'textarea' | 'number' | 'select' | 'toggle' | 'chips' | 'image' | 'json';

export interface CrudField {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  placeholder?: string;
  help?: string; // บอก "ช่องนี้ใช้ทำอะไร / ใช้ตอนไหน"
  options?: { value: string; label: string }[]; // สำหรับ type=select
  default?: unknown;
  fullWidth?: boolean; // กินเต็มแถว (textarea/chips)
  disabledOnEdit?: boolean; // key ที่ห้ามแก้ตอน edit (เช่น brand_key, colorKey)
  insertVars?: string[]; // (textarea) ปุ่ม chip แทรก {{var}} — admin ไม่ต้องพิมพ์ syntax เอง
  money?: boolean; // (number) เก็บเป็นสตางค์ใน DB แต่กรอก/แสดงเป็นบาท (×100)
}

interface CrudApi<T> {
  list: (brand?: Brand) => Promise<T[]>;
  create: (data: Partial<T>) => Promise<unknown>;
  update: (id: number, patch: Partial<T>) => Promise<unknown>;
  remove: (id: number, hard?: boolean) => Promise<unknown>;
}

interface CrudManagerProps<T extends { id: number }> {
  title: string;
  help?: ReactNode;
  queryKey: string;
  api: CrudApi<T>;
  fields: CrudField[];
  defaults?: Partial<T>;
  brandScoped?: boolean; // โชว์ filter แบรนด์ + ส่ง brand ให้ list + เซ็ต default brand
  brandAll?: boolean; // มีตัวเลือก "ทุกแบรนด์" (เช่น FAQ ที่ใช้ได้ทุกแบรนด์) + default = all
  searchKeys?: (keyof T)[]; // เปิด search ถ้ามี
  groupBy?: (row: T) => string; // จัดกลุ่มในการแสดงผล
  rowTitle: (row: T) => ReactNode;
  rowSubtitle?: (row: T) => ReactNode;
  rowBadges?: (row: T) => ReactNode;
  sortRows?: (a: T, b: T) => number;
  hardDelete?: boolean; // ลบถาวร (default soft delete ถ้ามี field active)
  renderRowExtra?: (row: T) => ReactNode; // panel ขยายต่อแถว (เช่น Colors → size stock)
  addLabel?: string;
  headerExtra?: ReactNode | ((brand: Brand) => ReactNode); // slot ใต้ help (เช่น suggester, FB sync, filter chips) — รับ brand ปัจจุบันได้
  canCreate?: boolean; // ปิดปุ่ม "เพิ่ม" (เช่น KB ที่สร้างไม่ได้) default true
  canEdit?: boolean; // ซ่อนปุ่ม แก้/ลบ/เปิด-ปิด (เช่น viewer read-only) default true
  multiSelect?: boolean; // เลือกหลายรายการ → ลบทีเดียว
  // โหมดแบ่งหน้า (ข้อมูลเยอะ เช่น KB) — ดึงทีละหน้าจาก server (search ฝั่ง server)
  paginated?: { fetch: (o: { brand: Brand; q: string; page: number }) => Promise<{ items: T[]; total: number }>; pageSize: number };
}

export function CrudManager<T extends { id: number; active?: boolean | number }>(
  props: CrudManagerProps<T>,
) {
  const {
    title, help, queryKey, api, fields, defaults, brandScoped, searchKeys, groupBy,
    rowTitle, rowSubtitle, rowBadges, sortRows, hardDelete, renderRowExtra, addLabel,
    headerExtra, canCreate = true, canEdit = true, multiSelect, brandAll, paginated,
  } = props;
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);

  const qc = useQueryClient();
  const { brands } = useBrands();
  const [brand, setBrand] = useState<Brand>(brandAll ? 'all' : 'scrub');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<'new' | T | null>(null);
  const [draft, setEditingDraft] = useState<Record<string, unknown>>({});
  const [expanded, setExpanded] = useState<number | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);

  // auto-reorder: ถ้า schema มี field ลำดับ (priority/sortOrder) → เปิดช่องเลขแก้ได้ + ลากสลับ ให้ทุกหน้าอัตโนมัติ
  const orderField = !paginated
    ? fields.find((f) => f.type === 'number' && /^(priority|sortorder|sort_order)$/i.test(f.key))?.key
    : undefined;
  const orderDir: 'asc' | 'desc' = orderField && /priority/i.test(orderField) ? 'desc' : 'asc';

  const qKey = paginated ? [queryKey, brand, search, page] : (brandScoped ? [queryKey, brand] : [queryKey]);
  const { data: raw, isLoading } = useQuery<unknown>({
    queryKey: qKey,
    queryFn: async (): Promise<unknown> => (paginated ? paginated.fetch({ brand, q: search, page }) : api.list(brandScoped ? brand : undefined)),
  });
  const data: T[] = ((paginated ? (raw as { items: T[] } | undefined)?.items : (raw as T[] | undefined)) ?? []) as T[];
  const total = paginated ? ((raw as { total: number } | undefined)?.total ?? 0) : data.length;
  const totalPages = paginated ? Math.max(1, Math.ceil(total / paginated.pageSize)) : 1;

  const hasActive = fields.some((f) => f.key === 'active');
  const invalidate = () => qc.invalidateQueries({ queryKey: [queryKey] });

  const createMut = useMutation({
    mutationFn: (d: Partial<T>) => api.create(d),
    onSuccess: () => { invalidate(); setEditing(null); toast.success('เพิ่มแล้ว'); },
    onError: (e: Error) => toast.error('เพิ่มไม่สำเร็จ: ' + e.message),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<T> }) => api.update(id, patch),
    onSuccess: () => { invalidate(); setEditing(null); toast.success('บันทึกแล้ว'); },
    onError: (e: Error) => toast.error('บันทึกไม่สำเร็จ: ' + e.message),
  });
  const removeMut = useMutation({
    mutationFn: (id: number) => api.remove(id, hardDelete),
    onSuccess: () => { invalidate(); toast.success(hardDelete ? 'ลบแล้ว' : 'ปิดการใช้งานแล้ว'); },
    onError: (e: Error) => toast.error('ลบไม่สำเร็จ: ' + e.message),
  });
  const ord = (r: T) => Number((r as Record<string, unknown>)[orderField as string]) || 0;
  // อัปเดตหลายแถวเงียบๆ (เรียงลำดับใหม่)
  const reorderMut = useMutation({
    mutationFn: async (ups: { id: number; value: number }[]) => {
      await Promise.all(ups.map((u) => api.update(u.id, { [orderField as string]: u.value } as Partial<T>)));
    },
    onSuccess: () => { invalidate(); toast.success('เรียงลำดับใหม่แล้ว'); },
    onError: (e: Error) => toast.error('เรียงลำดับไม่สำเร็จ: ' + e.message),
  });
  // จัดลำดับใหม่ "ภายในกลุ่ม": ใช้ชุดค่าลำดับเดิมของกลุ่ม กระจายตามตำแหน่งใหม่ (ไม่กระทบกลุ่มอื่น)
  function reorderGroup(groupRows: T[], newSeq: T[]) {
    const slots = groupRows.map(ord).slice().sort((a, b) => (orderDir === 'desc' ? b - a : a - b));
    const ups = newSeq
      .map((r, i) => ({ id: r.id, value: slots[i]! }))
      .filter((u, i) => ord(newSeq[i]!) !== u.value);
    if (ups.length) reorderMut.mutate(ups);
  }
  function moveWithin(groupRows: T[], fromIdx: number, toIdx: number) {
    if (fromIdx < 0 || toIdx < 0 || toIdx >= groupRows.length || fromIdx === toIdx) return;
    const a = [...groupRows];
    const [x] = a.splice(fromIdx, 1);
    a.splice(toIdx, 0, x!);
    reorderGroup(groupRows, a);
  }
  function onDropRow(groupRows: T[], targetId: number) {
    const from = groupRows.findIndex((r) => r.id === dragId);
    const to = groupRows.findIndex((r) => r.id === targetId);
    setDragId(null);
    if (from < 0 || to < 0) return; // ลากข้ามกลุ่ม → ไม่ทำ
    moveWithin(groupRows, from, to);
  }

  // ── filter + sort + group ──
  const rows = useMemo(() => {
    let r = [...data];
    if (!paginated && search && searchKeys) {
      const s = search.toLowerCase();
      r = r.filter((row) => searchKeys.some((k) => String(row[k] ?? '').toLowerCase().includes(s)));
    }
    if (sortRows) r.sort(sortRows);
    else if (orderField) r.sort((a, b) => {
      const av = Number((a as Record<string, unknown>)[orderField]) || 0;
      const bv = Number((b as Record<string, unknown>)[orderField]) || 0;
      return orderDir === 'desc' ? bv - av : av - bv;
    });
    return r;
  }, [data, search, searchKeys, sortRows, paginated, orderField, orderDir]);

  const groups = useMemo(() => {
    if (!groupBy) return [['', rows]] as [string, T[]][];
    const map = new Map<string, T[]>();
    for (const row of rows) {
      const g = groupBy(row);
      (map.get(g) ?? map.set(g, []).get(g)!).push(row);
    }
    return [...map.entries()];
  }, [rows, groupBy]);

  const jsonKeys = fields.filter((f) => f.type === 'json').map((f) => f.key);
  const toEditable = (obj: Record<string, unknown>) => {
    const d = { ...obj };
    for (const k of jsonKeys) d[k] = d[k] == null ? '' : (typeof d[k] === 'string' ? d[k] : JSON.stringify(d[k], null, 2));
    return d;
  };

  function startNew() {
    const init: Record<string, unknown> = { ...defaults };
    for (const f of fields) if (init[f.key] === undefined && f.default !== undefined) init[f.key] = f.default;
    if (brandScoped && init.brand === undefined) init.brand = brand;
    setEditing('new');
    setEditingDraft(toEditable(init));
  }

  function startEdit(row: T) {
    setEditing(row);
    setEditingDraft(toEditable(row as Record<string, unknown>));
  }
  function save() {
    for (const f of fields) {
      if (f.required && !String(draft[f.key] ?? '').trim()) {
        toast.error(`กรุณากรอก: ${f.label}`);
        return;
      }
    }
    const payload: Record<string, unknown> = { ...draft };
    for (const k of jsonKeys) {
      const raw = String(payload[k] ?? '').trim();
      if (!raw) { payload[k] = null; continue; }
      try { payload[k] = JSON.parse(raw); } catch { toast.error(`JSON ไม่ถูกต้องในช่อง: ${fields.find((f) => f.key === k)?.label}`); return; }
    }
    if (editing && editing !== 'new' && typeof editing === 'object') {
      updateMut.mutate({ id: editing.id, patch: payload as Partial<T> });
    } else {
      createMut.mutate(payload as Partial<T>);
    }
  }

  return (
    <div className="space-y-4">
      {/* header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-semibold">{title}</h2>
        <div className="flex items-center gap-2">
          {brandScoped && (
            <select
              className="border rounded-md px-2 py-1.5 text-sm"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            >
              {brandAll && <option value="all">🌐 ทุกแบรนด์</option>}
              {brands.map((b) => (
                <option key={b.brand_key} value={b.brand_key}>{brandEmoji(b.brand_key)} {b.name}</option>
              ))}
            </select>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => (editing === 'new' ? setEditing(null) : startNew())}>
              <Plus size={14} className="mr-1" /> {addLabel ?? 'เพิ่ม'}
            </Button>
          )}
        </div>
      </div>

      {help && <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-3">{help}</div>}
      {typeof headerExtra === 'function' ? headerExtra(brand) : headerExtra}

      {/* bulk action bar */}
      {multiSelect && selected.size > 0 && (
        <div className="sticky top-0 z-10 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
          <span>เลือก {selected.size} รายการ</span>
          <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => { if (confirm(`ลบ ${selected.size} รายการ?`)) { selected.forEach((id) => removeMut.mutate(id)); setSelected(new Set()); } }}>ลบที่เลือก</Button>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => setSelected(new Set())}>ยกเลิก</Button>
        </div>
      )}

      {/* ฟอร์มเพิ่ม/แก้ — บนสุด */}
      {editing && (
        <div className="border rounded-lg p-4 space-y-3 bg-card">
          <h3 className="font-semibold text-sm">
            {editing === 'new' || typeof editing !== 'object' ? '➕ เพิ่มรายการใหม่' : '✏️ แก้ไขรายการ'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fields.map((f) => {
              const disabled = f.disabledOnEdit && editing !== 'new' && typeof editing === 'object';
              const val = draft[f.key];
              const set = (v: unknown) => setEditingDraft((d) => ({ ...d, [f.key]: v }));
              const wrap = (node: ReactNode) => (
                <div key={f.key} className={f.fullWidth || f.type === 'textarea' || f.type === 'image' ? 'md:col-span-2' : ''}>
                  <label className="text-xs font-medium text-muted-foreground">
                    {f.label}{f.required && <span className="text-red-500"> *</span>}
                  </label>
                  {node}
                  {f.help && <p className="text-[11px] text-muted-foreground mt-0.5">{f.help}</p>}
                </div>
              );
              if (f.type === 'textarea')
                return wrap(
                  <>
                    <textarea className="w-full border border-input rounded-md p-2 text-sm min-h-[100px]" value={String(val ?? '')} placeholder={f.placeholder} disabled={disabled} onChange={(e) => set(e.target.value)} />
                    {f.insertVars && f.insertVars.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className="text-[11px] text-muted-foreground self-center">แทรกตัวแปร:</span>
                        {f.insertVars.map((v) => (
                          <button key={v} type="button" className="text-[11px] px-1.5 py-0.5 rounded border bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100" onClick={() => set(String(val ?? '') + `{{${v}}}`)}>{`{{${v}}}`}</button>
                        ))}
                      </div>
                    )}
                  </>,
                );
              if (f.type === 'number') {
                const num = val === undefined || val === null ? '' : (f.money ? Number(val) / 100 : Number(val));
                return wrap(
                  <div className="flex items-center gap-1">
                    <Input type="number" value={num} placeholder={f.placeholder} disabled={disabled} onChange={(e) => set(e.target.value === '' ? null : (f.money ? Math.round(Number(e.target.value) * 100) : Number(e.target.value)))} />
                    {f.money && <span className="text-xs text-muted-foreground">฿</span>}
                  </div>,
                );
              }
              if (f.type === 'json')
                return wrap(<textarea className="w-full border border-input rounded-md p-2 text-xs font-mono min-h-[90px]" value={String(val ?? '')} placeholder={f.placeholder ?? '{ }'} disabled={disabled} onChange={(e) => set(e.target.value)} />);
              if (f.type === 'toggle')
                return wrap(
                  <button type="button" onClick={() => set(!(val === true || val === 1))} className={`mt-1 px-3 py-1.5 rounded-md text-sm border ${val === true || val === 1 ? 'bg-green-100 border-green-300 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                    {val === true || val === 1 ? '✅ เปิด' : '⚪ ปิด'}
                  </button>,
                );
              if (f.type === 'select')
                return wrap(
                  <select className="w-full border rounded-md px-2 py-2 text-sm" value={String(val ?? '')} disabled={disabled} onChange={(e) => set(e.target.value)}>
                    <option value="">— เลือก —</option>
                    {(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>,
                );
              if (f.type === 'image')
                return wrap(<ImageAttach brand={(draft.brand as 'scrub' | 'bow') === 'bow' ? 'bow' : 'scrub'} value={(val as string) ?? null} onChange={(url) => set(url)} />);
              if (f.type === 'chips') {
                const items = String(val ?? '').split(',').map((s) => s.trim()).filter(Boolean);
                return wrap(
                  <>
                    <Input value={String(val ?? '')} placeholder={f.placeholder ?? 'คั่นด้วย ,'} disabled={disabled} onChange={(e) => set(e.target.value)} />
                    {items.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {items.map((it, i) => <Badge key={i} variant="outline" className="text-[10px]">{it}</Badge>)}
                      </div>
                    )}
                  </>,
                );
              }
              return wrap(<Input value={String(val ?? '')} placeholder={f.placeholder} disabled={disabled} onChange={(e) => set(e.target.value)} />);
            })}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={createMut.isPending || updateMut.isPending}>บันทึก</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(null)}>ยกเลิก</Button>
          </div>
        </div>
      )}

      {/* search */}
      {(searchKeys || paginated) && (
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8" placeholder="🔍 ค้นหา…" value={search} onChange={(e) => { setSearch(e.target.value); if (paginated) setPage(1); }} />
        </div>
      )}

      {/* list */}
      {isLoading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">กำลังโหลด…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg border-dashed">ยังไม่มีข้อมูล — กด “{addLabel ?? 'เพิ่ม'}” เพื่อสร้างรายการแรก</div>
      ) : (
        <div className="space-y-4">
          {groups.map(([g, gr]) => (
            <div key={g || '_'} className="space-y-1.5">
              {g && <div className="text-xs font-semibold text-muted-foreground pt-1">{g} <span className="font-normal">({gr.length})</span></div>}
              {gr.map((row, i) => {
                const isOn = row.active === undefined ? true : row.active === true || row.active === 1;
                return (
                  <div
                    key={row.id}
                    onDragOver={orderField ? (e) => e.preventDefault() : undefined}
                    onDrop={orderField ? () => onDropRow(gr, row.id) : undefined}
                    className={`border rounded-lg ${isOn ? '' : 'opacity-50'} ${dragId === row.id ? 'ring-2 ring-primary/40' : ''}`}
                  >
                    <div className="flex items-start gap-2 p-3">
                      {multiSelect && (
                        <input type="checkbox" className="mt-1 shrink-0" checked={selected.has(row.id)} onChange={() => setSelected((s) => { const n = new Set(s); n.has(row.id) ? n.delete(row.id) : n.add(row.id); return n; })} />
                      )}
                      {orderField ? (
                        <div className="flex items-center gap-0.5 shrink-0 pt-0.5">
                          <span
                            draggable
                            onDragStart={() => setDragId(row.id)}
                            onDragEnd={() => setDragId(null)}
                            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
                            title="ลากเพื่อสลับลำดับ"
                          >
                            <GripVertical size={15} />
                          </span>
                          <input
                            type="number"
                            min={1}
                            key={`${row.id}-${i}`}
                            defaultValue={i + 1}
                            title="ลำดับ — พิมพ์เลขตำแหน่งใหม่ หรือลากไอคอนซ้าย"
                            className="w-11 border rounded px-1 py-0.5 text-xs text-center bg-background"
                            onBlur={(e) => { const to = Number(e.target.value) - 1; if (!Number.isNaN(to) && to !== i) moveWithin(gr, i, to); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground font-mono w-6 shrink-0 pt-0.5">{i + 1}</span>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{rowTitle(row)}</span>
                          {rowBadges?.(row)}
                          {!isOn && <Badge variant="outline" className="text-[10px]">ปิดอยู่</Badge>}
                        </div>
                        {rowSubtitle && <div className="text-xs text-muted-foreground mt-0.5 break-words">{rowSubtitle(row)}</div>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {renderRowExtra && (
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                            {expanded === row.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </Button>
                        )}
                        {canEdit && hasActive && (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => updateMut.mutate({ id: row.id, patch: { active: !isOn } as Partial<T> })}>
                            {isOn ? 'ปิด' : 'เปิด'}
                          </Button>
                        )}
                        {canEdit && <Button size="sm" variant="outline" className="h-7 px-2" title="แก้" onClick={() => startEdit(row)}><Pencil size={13} /></Button>}
                        {canEdit && <Button size="sm" variant="outline" className="h-7 px-2 text-red-600" title={hardDelete ? 'ลบถาวร' : 'ปิด/ลบ'} onClick={() => confirm('ยืนยันลบรายการนี้?') && removeMut.mutate(row.id)}><Trash2 size={13} /></Button>}
                      </div>
                    </div>
                    {renderRowExtra && expanded === row.id && <div className="border-t p-3 bg-muted/20">{renderRowExtra(row)}</div>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* pagination */}
      {paginated && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← ก่อนหน้า</Button>
          <span className="text-muted-foreground">หน้า {page} / {totalPages} ({total.toLocaleString()} รายการ)</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>ถัดไป →</Button>
        </div>
      )}
    </div>
  );
}

// helper: สร้าง options แบรนด์จาก useBrands (ใช้ใน field select brand)
export function useBrandOptions(includeAll = false) {
  const { brands } = useBrands();
  const opts = brands.map((b) => ({ value: b.brand_key, label: `${brandEmoji(b.brand_key)} ${b.name}` }));
  return includeAll ? [{ value: 'all', label: '🌐 ทุกแบรนด์' }, ...opts] : opts;
}

export { brandLabel };
